/*
  HAPPY DOG cart drawer (v1) - replaces EliteCart.
  - Renders through the Section Rendering API (sections/hd-cart-drawer.liquid).
  - Catches every product form posting to /cart/add, adds by AJAX and opens the drawer.
  - Refreshes in the background when other code writes to the cart.
  - Keeps the welcome gift rules (one gift, only with a subscription).
  - Applies codes set in the product metafield custom.elitecart_auto_apply_discount_code.
  - window.HDCart is the public API; window.EliteCart is a shim for existing buy boxes.
*/
(function () {
  'use strict';
  // Note: <hd-cart id="HDCart"> makes window.HDCart an element, so guard on a flag instead
  if (window.__hdCartBooted) return;
  window.__hdCartBooted = true;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var nativeFetch = window.fetch.bind(window);
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var el = null;          // <hd-cart>
  var busy = 0;           // in-flight internal requests
  var refreshTimer = null;
  var pendingOpen = false;
  var waiting = [];       // resolvers for coalesced refresh calls
  var listeners = [];     // EliteCart.onCartChange shim callbacks
  var triedCodes = {};    // auto-apply attempts this page (code -> count)
  var removedCodes = {};  // codes the customer removed this session; never re-applied
  try { removedCodes = JSON.parse(sessionStorage.getItem('hdRemovedCodes') || '{}') || {}; } catch (e) {}
  var enforcing = false;

  function json(url, body) {
    busy++;
    return nativeFetch(url, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) { var e = new Error(data.description || data.message || 'Cart error'); e.data = data; throw e; }
        return data;
      });
    }).finally(function () { busy--; });
  }

  function getCart() { return json(root + 'cart.js'); }

  function setLoading(on) {
    if (el) el.classList.toggle('is-loading', !!on);
  }

  /* ---------- Rendering ---------- */
  function render() {
    busy++;
    setLoading(true);
    return nativeFetch(root + '?sections=hd-cart-drawer,cart-icon-bubble', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var doc = new DOMParser().parseFromString(sections['hd-cart-drawer'] || '', 'text/html');
        var fresh = doc.getElementById('HDCart-Body');
        var body = document.getElementById('HDCart-Body');
        if (fresh && body) body.innerHTML = fresh.innerHTML;

        if (sections['cart-icon-bubble']) {
          var bdoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
          var bsec = bdoc.querySelector('.shopify-section');
          var bubble = document.getElementById('cart-icon-bubble');
          if (bsec && bubble) bubble.innerHTML = bsec.innerHTML;
        }
      })
      .finally(function () { busy--; setLoading(false); })
      .then(afterRender);
  }

  function readState() {
    var node = el && el.querySelector('[data-hd-state]');
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (e) { return null; }
  }

  function afterRender() {
    var state = readState();
    if (!state) return;
    var chain = Promise.resolve(false);
    if (!enforcing) chain = enforceGifts(state);
    return chain.then(function (changedGifts) {
      if (changedGifts) return render();
      return syncPerk(state).then(function (changedPerk) {
        if (changedPerk) return render();
        return applyAutoCodes(state);
      });
    }).then(function () {
      notify();
    }).catch(function (e) { console.warn('[HDCart]', e); });
  }

  function notify() {
    getCart().then(function (cart) {
      listeners.forEach(function (cb) { try { cb(cart); } catch (e) {} });
      document.dispatchEvent(new CustomEvent('hd:cart:updated', { detail: { cart: cart } }));
    }).catch(function () {});
  }

  /* ---------- Rules ---------- */
  function enforceGifts(state) {
    var gifts = state.gifts || [];
    if (!gifts.length) return Promise.resolve(false);
    var changes = [];
    if (!state.hasSub) {
      gifts.forEach(function (g) { changes.push([g.key, 0]); });
    } else {
      gifts.forEach(function (g, i) {
        if (i > 0) changes.push([g.key, 0]);
        else if (g.qty > 1) changes.push([g.key, 1]);
      });
    }
    if (!changes.length) return Promise.resolve(false);
    enforcing = true;
    return changes.reduce(function (p, c) {
      return p.then(function () { return json(root + 'cart/change.js', { id: c[0], quantity: c[1] }); });
    }, Promise.resolve()).then(function () { return true; })
      .finally(function () { enforcing = false; });
  }

  // Double up perk: free Priority Pick & Pack is recorded as a cart attribute (it carries to the order,
  // where Shopify Flow adds the tag). A paid Pick & Pack line is removed so it is never charged twice.
  var perkTries = 0;
  var PERK_KEY = 'Priority Pick & Pack';
  var THEME_KEY = 'Theme';
  var THEME_VAL = 'New cart';
  var UPSELL_KEY = 'Basket upsell';
  function syncPerk(state) {
    if (!('perk' in state) || perkTries >= 3) return Promise.resolve(false);
    var jobs = [];
    var attrs = {};
    if (state.perk && !state.perkAttr) { attrs[PERK_KEY] = 'Free with Double up'; jobs.push(attrs); }
    if (!state.perk && state.perkAttr) { attrs[PERK_KEY] = ''; jobs.push(attrs); }
    // Marks every basket on this theme so Shopify Flow can tag its orders "New theme"
    if (state.itemCount && !state.themeAttr) { attrs[THEME_KEY] = THEME_VAL; jobs.push(attrs); }
    // Basket upsell tracking: set while a line added from "Customers also add" is in the basket, cleared when it goes
    if (!!state.upsell !== !!state.upsellAttr) { attrs[UPSELL_KEY] = state.upsell ? 'Added' : ''; jobs.push(attrs); }
    var p = Promise.resolve();
    if (jobs.length) p = p.then(function () { return json(root + 'cart/update.js', { attributes: attrs }); });
    if (state.perk && state.perkLine) {
      jobs.push(1);
      p = p.then(function () { return json(root + 'cart/change.js', { id: state.perkLine, quantity: 0 }); });
    }
    if (!jobs.length) { perkTries = 0; return Promise.resolve(false); } // in sync: reset the loop guard
    perkTries++;
    return p.then(function () { return true; }, function () { return false; });
  }

  function applyAutoCodes(state) {
    // Re-apply auto codes whenever they go missing (e.g. another script
    // overwrote the code list), unless the customer removed them.
    // Capped per page so a rejected code can never cause a loop.
    var wanted = (state.autoCodes || []).filter(function (c) {
      var k = c && c.toUpperCase();
      return k && !removedCodes[k] && (triedCodes[k] || 0) < 3;
    });
    if (!wanted.length) return Promise.resolve();
    return getCart().then(function (cart) {
      var have = (cart.discount_codes || []).map(function (d) { return d.code.toUpperCase(); });
      var missing = wanted.filter(function (c) { return have.indexOf(c.toUpperCase()) === -1; });
      if (!missing.length) return;
      missing.forEach(function (c) { var k = c.toUpperCase(); triedCodes[k] = (triedCodes[k] || 0) + 1; });
      return setCodes(currentCodes(cart).concat(missing)).then(render);
    });
  }

  function currentCodes(cart) {
    return (cart.discount_codes || []).map(function (d) { return d.code; });
  }

  function setCodes(codes) {
    var seen = {};
    var list = codes.filter(function (c) {
      var k = String(c).toUpperCase();
      if (!c || seen[k]) return false;
      seen[k] = true;
      return true;
    });
    return json(root + 'cart/update.js', { discount: list.join(',') });
  }

  function addCode(code) {
    if (!code) return Promise.resolve();
    return getCart().then(function (cart) {
      var codes = currentCodes(cart);
      if (codes.map(function (c) { return c.toUpperCase(); }).indexOf(code.toUpperCase()) !== -1) return cart;
      return setCodes(codes.concat([code]));
    });
  }

  /* ---------- Public actions ---------- */
  function open() {
    if (!el) return;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('hd-cart-locked');
    var panel = el.querySelector('.hd-cart__panel');
    if (panel) setTimeout(function () { panel.focus({ preventScroll: true }); }, 50);
    document.dispatchEvent(new CustomEvent('hd:cart:open'));
  }

  function close() {
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('hd-cart-locked');
  }

  function refresh(opts) {
    opts = opts || {};
    if (opts.open) pendingOpen = true;
    clearTimeout(refreshTimer);
    return new Promise(function (resolve) {
      waiting.push(resolve);
      refreshTimer = setTimeout(function () {
        var doOpen = pendingOpen;
        var done = waiting;
        pendingOpen = false;
        waiting = [];
        var finish = function () { done.forEach(function (r) { r(); }); };
        render().then(function () {
          if (doOpen) open();
          finish();
        }, finish);
      }, opts.now ? 0 : 120);
    });
  }

  function addItems(items, opts) {
    opts = opts || {};
    setLoading(true);
    return json(root + 'cart/add.js', { items: items })
      .then(function () { return opts.code ? addCode(opts.code) : null; })
      .then(function () { return refresh({ open: opts.open !== false, now: true }); })
      .catch(function (e) { toast(e.message); setLoading(false); });
  }

  function changeLine(key, qty) {
    setLoading(true);
    return json(root + 'cart/change.js', { id: key, quantity: qty })
      .then(function () { return refresh({ now: true }); })
      .catch(function (e) { toast(e.message); setLoading(false); });
  }

  function toast(msg) {
    if (!el || !msg) return;
    var t = el.querySelector('.hd-cart__toast');
    if (!t) {
      t = document.createElement('div');
      t.className = 'hd-cart__toast';
      t.setAttribute('role', 'alert');
      el.querySelector('.hd-cart__panel').appendChild(t);
    }
    t.textContent = msg;
    t.classList.add('is-shown');
    clearTimeout(t._h);
    t._h = setTimeout(function () { t.classList.remove('is-shown'); }, 4000);
  }

  /* ---------- Discount code box ---------- */
  function applyTypedCode(btn) {
    var box = btn.closest('.hd-cart__code-box');
    var input = box.querySelector('[data-hd-code-input]');
    var msg = box.querySelector('[data-hd-code-msg]');
    var code = (input.value || '').trim();
    if (!code) return;
    msg.textContent = '';
    setLoading(true);
    getCart().then(function (cart) {
      var codes = currentCodes(cart);
      if (codes.map(function (c) { return c.toUpperCase(); }).indexOf(code.toUpperCase()) !== -1) {
        msg.textContent = 'This discount code is already applied';
        setLoading(false);
        return;
      }
      return setCodes(codes.concat([code])).then(function (updated) {
        var hit = (updated.discount_codes || []).filter(function (d) { return d.code.toUpperCase() === code.toUpperCase(); })[0];
        if (!hit || !hit.applicable) {
          return setCodes(codes).then(function () {
            setLoading(false);
            msg.textContent = 'Invalid or expired discount code';
          });
        }
        return refresh({ now: true });
      });
    }).catch(function (e) { setLoading(false); msg.textContent = e.message; });
  }

  function removeCode(code) {
    setLoading(true);
    getCart().then(function (cart) {
      var codes = currentCodes(cart).filter(function (c) { return c.toUpperCase() !== code.toUpperCase(); });
      removedCodes[code.toUpperCase()] = true; // do not auto re-apply what the customer removed
      try { sessionStorage.setItem('hdRemovedCodes', JSON.stringify(removedCodes)); } catch (e) {}
      return setCodes(codes);
    }).then(function () { return refresh({ now: true }); })
      .catch(function () { setLoading(false); });
  }

  /* ---------- Event wiring ---------- */
  function onDrawerClick(e) {
    var t = e.target;
    if (t.closest('[data-hd-close]')) { close(); return; }

    var qty = t.closest('[data-hd-qty]');
    if (qty) {
      var line = qty.closest('[data-key]');
      if (line) changeLine(line.getAttribute('data-key'), parseInt(qty.getAttribute('data-hd-qty'), 10));
      return;
    }

    var toggle = t.closest('[data-hd-variant-toggle]');
    if (toggle) {
      var list = toggle.closest('[data-hd-upsell-card]').querySelector('.hd-cart__variant-list');
      var openNow = list.hasAttribute('hidden');
      list.toggleAttribute('hidden', !openNow);
      toggle.setAttribute('aria-expanded', openNow ? 'true' : 'false');
      return;
    }

    var chip = t.closest('[data-hd-variant]');
    if (chip) {
      var card = chip.closest('[data-hd-upsell-card]');
      card.querySelectorAll('[data-hd-variant]').forEach(function (c) {
        var on = c === chip;
        c.classList.toggle('is-selected', on);
        c.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      card.querySelector('[data-hd-add]').setAttribute('data-hd-add', chip.getAttribute('data-hd-variant'));
      card.querySelector('[data-hd-variant-label]').textContent = chip.getAttribute('data-hd-variant-title');
      card.querySelector('[data-hd-price]').textContent = chip.getAttribute('data-hd-variant-price');
      var was = card.querySelector('.hd-cart__upsell-price s');
      if (was) was.remove();
      card.querySelector('.hd-cart__variant-list').setAttribute('hidden', '');
      card.querySelector('[data-hd-variant-toggle]').setAttribute('aria-expanded', 'false');
      return;
    }

    // Double up: swap the line for the bigger pack (same price as the product page)
    var swap = t.closest('[data-hd-swap]');
    if (swap) {
      var sLine = swap.closest('[data-key]');
      var item = { id: parseInt(swap.getAttribute('data-hd-swap'), 10), quantity: 1, properties: { _hd_double_up: swap.getAttribute('data-hd-from') || 'Yes' } };
      if (swap.getAttribute('data-hd-perk-ok')) item.properties._hd_perk = 'Yes'; // free Priority Pick & Pack (dry bags doubled, tins to 36)
      var plan = swap.getAttribute('data-hd-plan');
      if (plan) item.selling_plan = parseInt(plan, 10);
      var items = [item];
      var perk = swap.getAttribute('data-hd-perk');
      if (perk) items.push({ id: parseInt(perk, 10), quantity: 1 }); // free Priority Pick & Pack with a double up
      swap.disabled = true;
      setLoading(true);
      json(root + 'cart/add.js', { items: items })
        .then(function () { return sLine ? json(root + 'cart/change.js', { id: sLine.getAttribute('data-key'), quantity: 0 }) : null; })
        .then(function () { return refresh({ now: true }); })
        .catch(function (e) { toast(e.message); setLoading(false); swap.disabled = false; });
      return;
    }

    // Undo a double up: put the original pack back and remove the bigger one (the free perk goes with it)
    var undo = t.closest('[data-hd-undo]');
    if (undo) {
      var uLine = undo.closest('[data-key]');
      var back = { id: parseInt(undo.getAttribute('data-hd-undo'), 10), quantity: 1 };
      var uPlan = undo.getAttribute('data-hd-plan');
      if (uPlan) back.selling_plan = parseInt(uPlan, 10);
      undo.disabled = true;
      setLoading(true);
      json(root + 'cart/add.js', { items: [back] })
        .then(function () { return uLine ? json(root + 'cart/change.js', { id: uLine.getAttribute('data-key'), quantity: 0 }) : null; })
        .then(function () { return refresh({ now: true }); })
        .catch(function (e) { toast(e.message); setLoading(false); undo.disabled = false; });
      return;
    }

    var add = t.closest('[data-hd-add]');
    if (add) {
      var id = add.getAttribute('data-hd-add');
      add.disabled = true;
      var it = { id: parseInt(id, 10), quantity: 1 };
      if (add.hasAttribute('data-hd-upsell')) it.properties = { _hd_upsell: 'Basket' }; // "Customers also add" card, traced on the order
      addItems([it], { code: add.getAttribute('data-hd-code') || '' });
      return;
    }

    if (t.closest('[data-hd-apply-code]')) { applyTypedCode(t.closest('[data-hd-apply-code]')); return; }

    var rc = t.closest('[data-hd-remove-code]');
    if (rc) { removeCode(rc.getAttribute('data-hd-remove-code')); return; }

    if (t.closest('[data-hd-checkout]')) {
      t.closest('[data-hd-checkout]').classList.add('is-busy');
      window.location.href = root + 'checkout';
    }
  }

  function onDrawerChange(e) {
    var tog = e.target.closest('[data-hd-toggle]');
    if (!tog) return;
    if (tog.checked) {
      addItems([{ id: parseInt(tog.getAttribute('data-hd-toggle'), 10), quantity: 1 }],
        { open: false, code: tog.getAttribute('data-hd-code') || '' });
    } else if (tog.getAttribute('data-hd-key')) {
      changeLine(tog.getAttribute('data-hd-key'), 0);
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape' && el && el.classList.contains('is-open')) close();
    if (e.key === 'Enter' && e.target.matches && e.target.matches('[data-hd-code-input]')) {
      e.preventDefault();
      applyTypedCode(e.target.closest('.hd-cart__code-box').querySelector('[data-hd-apply-code]'));
    }
  }

  // Every product form posting to /cart/add (theme, Recharge widget, landing pages)
  function onSubmit(e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
    if (form.hasAttribute('data-hd-native')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (form.getAttribute('data-hd-busy') === '1') return;
    form.setAttribute('data-hd-busy', '1');

    var btn = e.submitter || form.querySelector('[type="submit"]');
    if (btn) { btn.setAttribute('aria-busy', 'true'); btn.classList.add('loading'); }
    var errBox = form.querySelector('.product-form__error-message-wrapper') ||
      (form.closest('product-form') && form.closest('product-form').querySelector('.product-form__error-message-wrapper'));
    if (errBox) errBox.setAttribute('hidden', '');

    busy++;
    setLoading(true);
    nativeFetch(root + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form)
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok || data.status) throw new Error(data.description || data.message || 'Could not add to basket');
        return data;
      });
    }).then(function () {
      return refresh({ open: true, now: true });
    }).catch(function (err) {
      setLoading(false);
      var msgEl = errBox && errBox.querySelector('.product-form__error-message');
      if (errBox && msgEl) { msgEl.textContent = err.message; errBox.removeAttribute('hidden'); }
      else { open(); toast(err.message); }
    }).finally(function () {
      busy--;
      form.removeAttribute('data-hd-busy');
      if (btn) { btn.removeAttribute('aria-busy'); btn.classList.remove('loading'); }
      document.querySelectorAll('.loading__spinner').forEach(function (s) { s.classList.add('hidden'); });
    });
  }

  // Header cart icon and the toggles older snippets click
  var ICON_SEL = '#cart-icon-bubble, .header__icon--cart, [data-cart-toggle], [data-ec-cart-toggle], [data-ec-cart-trigger], .ec-cart-icon';
  function onDocClick(e) {
    var icon = e.target.closest && e.target.closest(ICON_SEL);
    if (!icon || (el && el.contains(icon))) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    open();
  }

  // Refresh in the background whenever other code writes to the cart
  function watchExternalWrites() {
    var current = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = current.apply(this, arguments);
      if (CART_WRITE.test(url)) p.then(function () { refresh(); }, function () {});
      return p;
    };
    var xo = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (url && CART_WRITE.test(String(url))) {
        this.addEventListener('loadend', function () { refresh(); });
      }
      return xo.apply(this, arguments);
    };
  }

  function installShim() {
    if (window.EliteCart) return; // the real app is still running
    var shim = {
      openDrawerWithDataRefresh: function () { return refresh({ open: true, now: true }); },
      refreshCart: function () { return refresh({ now: true }); },
      open: open,
      openCart: open,
      close: close,
      getCurrentCart: function () { return null; },
      onCartChange: function (cb) { if (typeof cb === 'function') listeners.push(cb); },
      __hd: true
    };
    window.EliteCart = shim;
    if (!window.EC) window.EC = { open: open };
  }

  function init() {
    el = document.getElementById('HDCart');
    if (!el) return;

    el.addEventListener('click', onDrawerClick);
    el.addEventListener('change', onDrawerChange);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('submit', onSubmit, true);
    document.addEventListener('click', onDocClick, true);

    ['cart:refresh', 'cart:updated', 'elite:cart:refresh'].forEach(function (n) {
      document.addEventListener(n, function () { refresh(); });
    });
    ['cart-drawer:open', 'cart:open', 'elite:cart:open'].forEach(function (n) {
      document.addEventListener(n, function () { refresh({ open: true }); });
    });

    watchExternalWrites();
    installShim();

    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble) { bubble.setAttribute('role', 'button'); bubble.setAttribute('aria-haspopup', 'dialog'); }

    var openNow = false;
    try { openNow = sessionStorage.getItem('hdCartOpen') === '1'; sessionStorage.removeItem('hdCartOpen'); } catch (e) {}

    // Apply the rules to whatever is already in the basket, then open if /cart sent us here
    var state = readState();
    var chain = (state && (state.gifts.length || state.autoCodes.length || state.perk || state.perkAttr || (state.itemCount && !state.themeAttr) || state.upsell || state.upsellAttr)) ? afterRender() : Promise.resolve();
    Promise.resolve(chain).then(function () { if (openNow) open(); });
  }

  window.HDCart = {
    open: open,
    close: close,
    refresh: function (opts) { return refresh(Object.assign({ now: true }, opts || {})); },
    add: addItems,
    change: changeLine,
    addCode: function (code) { return addCode(code).then(function () { return refresh({ now: true }); }); }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
